import { motion } from "framer-motion";
import { ArrowDown, MapPin, Clock } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-black scanlines"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="/shop/hero-banner.jpg"
          alt="Ted Coffee & Fun Spot"
          className="w-full h-full object-cover opacity-85 shot-enhance"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 bg-grid opacity-40" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-between pt-28 pb-16">
        <div className="flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-10 bg-sky-400" />
            <span className="font-mono text-[11px] tracking-[0.3em] text-sky-400 uppercase">
              INTERNET CAFE · ΧΑΛΚΙΔΑ · EST. 2014
            </span>
            <span className="hidden sm:inline-block h-px w-10 bg-sky-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-white text-5xl sm:text-7xl lg:text-[8.5rem] leading-[0.85] uppercase max-w-5xl"
          >
            <span className="text-sky-400 neon-glow">T</span>ED Coffee <br />
            <span className="text-sky-400 neon-glow">&amp;</span> Fun Spot.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed"
          >
            <span className="text-white font-semibold">Internet Cafe</span> στη Χαλκίδα.
            High-end PCs, competitive gaming, καφές και non-stop δράση.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#games"
              data-testid="hero-cta-games"
              className="group inline-flex items-center gap-3 bg-sky-400 text-black font-bold uppercase tracking-widest px-8 py-4 text-sm hover:bg-sky-300 transition-colors"
            >
              Καταλογος Παιχνιδιων
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-3 border border-zinc-700 text-white font-bold uppercase tracking-widest px-8 py-4 text-sm hover:border-sky-400 hover:text-sky-400 transition-colors"
            >
              Βρειτε Μας
            </a>
          </motion.div>
        </div>

        {/* Bottom info strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-white/10 pt-8"
        >
          <InfoCell icon={MapPin} label="LOCATION" value="Ιατρίδου 8, Χαλκίδα" />
          <InfoCell icon={Clock} label="OPEN" value="Πρωί → Late Night" />
          <InfoCell label="PCs" value="High-end / RGB" />
          <InfoCell label="VIBE" value="Games & gamers για κάθε γούστο" />
        </motion.div>
      </div>
    </section>
  );
};

const InfoCell = ({ icon: Icon, label, value }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-2 text-sky-400/80">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span className="font-mono text-[10px] tracking-[0.3em] uppercase">{label}</span>
    </div>
    <span className="text-white text-sm md:text-base font-medium">{value}</span>
  </div>
);
